import type { TModelNumbersData, TModelNumber, TModelNumberTableRow } from "types/modelNumbers.type"

/**
 * Formats dimension array as comma-separated values to preserve API semantics
 */
const formatDimension = (values: string[]): string => {
	return values.join(", ")
}

/**
 * Flattens a model number into a single table row
 */
export const flattenModelNumberToRows = (modelNumber: TModelNumber): TModelNumberTableRow[] => {
	return [
		{
			key: `${modelNumber.id}`,
			id: modelNumber.id,
			model_number: modelNumber.model_number,
			image_from_stevens_catalogue: modelNumber.image_from_stevens_catalogue,
			width: formatDimension(modelNumber.dimensions.width),
			height: formatDimension(modelNumber.dimensions.height),
			depth: formatDimension(modelNumber.dimensions.depth),
			min_size: modelNumber.dimensions.min_size ?? "",
			avg_size: modelNumber.dimensions.avg_size,
			max_size: modelNumber.dimensions.max_size ?? 0,
		},
	]
}

/**
 * Checks if a value is a nested category (object with subcategories)
 */
export const isNestedCategory = (value: unknown): value is { [key: string]: { [key: string]: TModelNumber[] } } => {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false
	}

	// Check if at least one property is an object (not array)
	const firstValue = Object.values(value as Record<string, unknown>)[0]
	return typeof firstValue === "object" && !Array.isArray(firstValue)
}

/**
 * Checks if a value is an array of model numbers
 */
export const isModelNumberArray = (value: unknown): value is TModelNumber[] => {
	return Array.isArray(value) && value.length > 0 && "id" in value[0]
}

/**
 * Processes model numbers data to extract table rows
 * Handles both nested categories and flat arrays
 */
export const extractModelNumberRows = (data: TModelNumbersData | TModelNumber[]): TModelNumberTableRow[] => {
	if (Array.isArray(data)) {
		// Handle "Without category" case
		return data.flatMap(flattenModelNumberToRows)
	}

	// Handle nested categories
	const rows: TModelNumberTableRow[] = []

	Object.values(data).forEach((topLevel) => {
		if (isNestedCategory(topLevel)) {
			Object.values(topLevel).forEach((nested) => {
				if (typeof nested === "object" && nested !== null) {
					Object.values(nested).forEach((subCategory) => {
						if (isModelNumberArray(subCategory)) {
							rows.push(...subCategory.flatMap(flattenModelNumberToRows))
						}
					})
				}
			})
		} else if (isModelNumberArray(topLevel)) {
			rows.push(...topLevel.flatMap(flattenModelNumberToRows))
		}
	})

	return rows
}
